import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import styles from "./mybooking.module.css";
import { MenuItem, Dialog, DialogTitle, DialogContent, FormControl, InputLabel, Select, Snackbar, Alert, Button, DialogActions, CircularProgress } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Calendar, X, Mail as MailIcon, AlertTriangle } from "react-feather";
import axios from "axios";
import { authData, withCustomerAuth } from "../../../admin/components/GetAuth";
import Loader from "../../../admin/components/Loader";
import SideBar from "../../components/sideBar";
import { MyBookingLoader } from "../../components/ContentLoader";
import { DateRangePicker } from "rsuite";
import dayjs from "dayjs";
import BookingBlock from "./components/BookingBlock";
import BookingDetailsPopup from "./components/bookingDetailsPopup";
import download from "downloadjs";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import { companyCountries } from "../../../utils/companyCountries";

const PAGE_SIZE = 15;

const MyBookings = () => {
  const auth = authData();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bookingList, setBookingList] = useState([]);
  const [listMeta, setListMeta] = useState({ page: 0, hasMore: false, total: 0, allTotal: 0 });
  const [filterVars, setFilterVars] = useState({ searchKey: "", status: "", date: null });
  const [searchQuery, setSearchQuery] = useState("");
  const requestId = useRef(0);
  const sentinelRef = useRef(null);
  const [viewModalOpen, setViewModalOpen] = useState();
  const [viewId, setViewId] = useState(0);
  const [viewStr, setViewStr] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
  const [invoiceMailId, setInvoiceMailId] = useState(0);
  const [invoiceMail, setInvoiceMail] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState();
  const [cancelId, setCancelId] = useState(0);

  const { websiteData } = useWebsite();
  useSiteBranding("My booking", websiteData);
  const countries = companyCountries(websiteData);

  // Search waits for the user to stop typing
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(filterVars?.searchKey?.trim() || ""), 400);
    return () => clearTimeout(timer);
  }, [filterVars?.searchKey]);

  // One page of bookings, filtered on the server (v1/customer/booking-list?page=…)
  const fetchPage = useCallback((page) => {
    const params = { page, per_page: PAGE_SIZE };
    if (searchQuery) params.search = searchQuery;
    if (filterVars?.status !== "" && filterVars?.status !== "all") params.status = filterVars.status;
    if (filterVars?.date) {
      params.date_from = dayjs(filterVars.date[0]).format("YYYY-MM-DD");
      params.date_to = dayjs(filterVars.date[1]).format("YYYY-MM-DD");
    }

    return axios.get(process.env.REACT_APP_API_URL + "v1/customer/booking-list", {
      params,
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
      },
    });
  }, [auth?.api_token, searchQuery, filterVars?.status, filterVars?.date]);

  // First page whenever the filters change
  useEffect(() => {
    const id = ++requestId.current;
    setListLoading(true);
    fetchPage(1)
      .then((response) => {
        if (id !== requestId.current) return;
        if (response.data.status === "SUCCESS") {
          const meta = response.data.meta || {};
          setBookingList(response.data.data || []);
          setListMeta({ page: 1, hasMore: !!meta.has_more, total: meta.total ?? 0, allTotal: meta.all_total ?? 0 });
        }
      })
      .catch(() => { })
      .finally(() => {
        if (id !== requestId.current) return;
        setListLoading(false);
        setContentLoading(false);
      });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!listMeta.hasMore || loadingMore || listLoading) return;
    const id = requestId.current;
    setLoadingMore(true);
    fetchPage(listMeta.page + 1)
      .then((response) => {
        // Filters changed while this page was loading
        if (id !== requestId.current) return;
        if (response.data.status === "SUCCESS") {
          const meta = response.data.meta || {};
          setBookingList((prev) => [...prev, ...(response.data.data || [])]);
          setListMeta((prev) => ({ ...prev, page: meta.current_page ?? prev.page + 1, hasMore: !!meta.has_more }));
        }
      })
      .catch(() => { })
      .finally(() => setLoadingMore(false));
  }, [fetchPage, listMeta.hasMore, listMeta.page, loadingMore, listLoading]);

  // Load the next page when the end of the list scrolls into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "300px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, bookingList.length]);

  const sendMailBooking = () => {
    setSendInvoiceModalOpen(false);
    setLoading(true);
    axios(process.env.REACT_APP_API_URL + "v1/customer/send-invoice/" + invoiceMailId, {
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
      },
    })
      .then((response) => {
        setLoading(false);
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType(response.data.status === "SUCCESS" ? "success" : "error");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const printBooking = (id) => {
    setLoading(true);
    axios(process.env.REACT_APP_API_URL + "v1/customer/booking-pdf/" + id, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
      },
    })
      .then((response) => {
        setLoading(false);
        const content = response.headers["content-type"];
        download(response.data, "booking-" + id + ".pdf", content);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const cancelBooking = () => {
    setLoading(true);
    setCancelModalOpen(false);

    axios
      .post(
        process.env.REACT_APP_API_URL + "v1/customer/cancel-booking/" + cancelId,
        {},
        {
          headers: {
            Authorization: `Bearer ${auth?.api_token}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        setSnackbarOpen(true);
        if (response.data.status === "SUCCESS") {
          setSnackbarMessage("Booking cancelled successfully.");
          setSnackbarType("success");

          setBookingList((prev) =>
            prev.map((i) => (i?.id === cancelId ? { ...i, status: -1 } : i))
          );

          setCancelId(0);
        } else {
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }

  return (
    <>
      <Loader open={loading} setOpen={setLoading} />

      <div className={`${styles.MainDivCustomer}`}>
        <div className={`${styles.Container}`}>
          <div className={`${styles.CusRow}`}>
            <div className={`${styles.CustomerSideBar}`}>
              <SideBar />
            </div>
            <div className={`${styles.CustomerDeSec}`}>
              <div className={`${styles.UserProCard2}`}>
                {contentLoading && <MyBookingLoader />}

                {!contentLoading && (
                  <div className={`${styles.BookingCard}`}>
                    <div className={styles.PageHead}>
                      <div>
                        <h1 className={styles.PageHeadTitle}>My bookings</h1>
                        <p className={styles.PageHeadCount}>
                          {listMeta.total} booking{listMeta.total === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>

                    <div className={styles.FilterBar}>
                      <div className={`${styles.BookListSearch}`}>
                        <input
                          type="text"
                          placeholder="Search by ID, name, or postcode"
                          value={filterVars?.searchKey}
                          onChange={(e) => setFilterVars((prev) => ({ ...prev, searchKey: e.target.value }))}
                        />
                        {filterVars?.searchKey !== "" && (
                          <button type="button" onClick={() => setFilterVars((prev) => ({ ...prev, searchKey: "" }))}>
                            <Close />
                          </button>
                        )}
                      </div>

                      <div className={styles.FilterSelect}>
                        <FormControl fullWidth>
                          <InputLabel>Filter by status</InputLabel>
                          <Select
                            value={filterVars?.status}
                            label="Filter by status"
                            onChange={(e) => setFilterVars((prev) => ({ ...prev, status: e.target.value }))}
                          >
                            <MenuItem value={"all"}>All</MenuItem>
                            <MenuItem value={-1}>Order cancelled</MenuItem>
                            <MenuItem value={0}>Order booked — {countries.pickup}</MenuItem>
                            <MenuItem value={1}>Item assigned to driver — {countries.pickup}</MenuItem>
                            <MenuItem value={2}>Item picked up — {countries.pickup}</MenuItem>
                            <MenuItem value={3}>Item in warehouse — {countries.pickup}</MenuItem>
                            <MenuItem value={4}>Item shipped from warehouse — {countries.pickup}</MenuItem>
                            <MenuItem value={9}>Item received at port — {countries.delivery}</MenuItem>
                            <MenuItem value={5}>Item received in warehouse — {countries.delivery}</MenuItem>
                            <MenuItem value={6}>Item out for delivery — {countries.delivery}</MenuItem>
                            <MenuItem value={8}>Partially delivered — {countries.delivery}</MenuItem>
                            <MenuItem value={7}>Delivered — {countries.delivery}</MenuItem>
                          </Select>
                        </FormControl>
                      </div>

                      <div className={styles.FilterDate}>
                        <DateRangePicker
                          format={"dd/MM/yyyy"}
                          placement={"bottomEnd"}
                          placeholder="Search by date"
                          caretAs={Calendar}
                          value={filterVars?.date}
                          showOneCalendar
                          block
                          onChange={(e) => setFilterVars((prev) => ({ ...prev, date: e }))}
                        />
                      </div>
                    </div>

                    {bookingList?.length > 0 && (
                      <div className={styles.BookingTable}>
                        <div className={styles.TableHead}>
                          <span className={styles.TableHeadCell}>Booking</span>
                          <span className={styles.TableHeadCell}>Status</span>
                          <span className={styles.TableHeadCell}>Date</span>
                          <span className={styles.TableHeadCell}>Total</span>
                          <span className={styles.TableHeadCell}>Due</span>
                          <span />
                        </div>

                        {bookingList
                          ?.map((item) => (
                            <BookingBlock
                              key={item.id}
                              item={item}
                              setViewModalOpen={setViewModalOpen}
                              setViewId={setViewId}
                              setViewStr={setViewStr}
                              setSendInvoiceModalOpen={setSendInvoiceModalOpen}
                              setInvoiceMailId={setInvoiceMailId}
                              setInvoiceMail={setInvoiceMail}
                              printBooking={printBooking}
                              setCancelId={setCancelId}
                              setCancelModalOpen={setCancelModalOpen}
                            />
                          ))}
                      </div>
                    )}

                    {/* Infinite scroll: reaching this loads the next page */}
                    {bookingList?.length > 0 && listMeta.hasMore && (
                      <div ref={sentinelRef} className={styles.LoadMore}>
                        {loadingMore && <CircularProgress size={22} />}
                      </div>
                    )}

                    {listLoading && bookingList?.length === 0 && (
                      <div className={styles.LoadMore}>
                        <CircularProgress size={22} />
                      </div>
                    )}

                    {!listLoading && bookingList?.length === 0 && (
                      <div className={`${styles.NofileSec}`}>
                        <div className={`${styles.NofileSecUnder}`}>
                          <img src="/images/no-connection.png" alt="" />
                          <p>
                            {listMeta.allTotal === 0
                              ? "You're all set to ship your first item"
                              : "No bookings match your filters"}
                          </p>
                          <span className={styles.NofileSub}>
                            {listMeta.allTotal === 0
                              ? "Once you book a shipment, you'll be able to track it right here."
                              : "Try adjusting your search, status, or date range."}
                          </span>
                          {listMeta.allTotal === 0 && (
                            <Link to="/customer/add-booking" className={styles.NofileCTA}>
                              Add a booking
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        fullWidth
        maxWidth="md"
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        PaperProps={{ className: styles.ModalPaper }}
      >
        <DialogTitle className={styles.ModalHead} component="div">
          <div className={styles.ModalHeadText}>
            <p className={styles.ModalEyebrow}>Order</p>
            <h2 className={styles.ModalTitle}>#{viewStr}</h2>
          </div>
          <button className={styles.ModalCloseBU} onClick={() => setViewModalOpen(false)} aria-label="Close">
            <X />
          </button>
        </DialogTitle>
        <DialogContent className={styles.ModalBody}>
          <BookingDetailsPopup bookingId={viewId} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={sendInvoiceModalOpen}
        onClose={() => setSendInvoiceModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: styles.ModalPaper }}
      >
        <DialogTitle className={styles.ModalHead} component="div">
          <div className={styles.ModalHeadText}>
            <p className={styles.ModalEyebrow}>Invoice</p>
            <h2 className={styles.ModalTitle}>Send invoice?</h2>
          </div>
          <button className={styles.ModalCloseBU} onClick={() => setSendInvoiceModalOpen(false)} aria-label="Close">
            <X />
          </button>
        </DialogTitle>
        <DialogContent className={styles.ModalBody}>
          <div className={`${styles.ModalIconWrap} ${styles.Brand}`}>
            <MailIcon size={20} />
          </div>
          <p className={styles.ModalText}>
            We'll email a copy of this booking's invoice to <strong>{invoiceMail}</strong>.
          </p>
        </DialogContent>
        <DialogActions className={styles.ModalActions}>
          <Button className={styles.ModalBtnGhost} onClick={() => setSendInvoiceModalOpen(false)}>
            Cancel
          </Button>
          <Button className={styles.ModalBtnPrimary} onClick={sendMailBooking}>
            Send invoice
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: styles.ModalPaper }}
      >
        <DialogTitle className={styles.ModalHead} component="div">
          <div className={styles.ModalHeadText}>
            <p className={styles.ModalEyebrow}>Booking</p>
            <h2 className={styles.ModalTitle}>Cancel this booking?</h2>
          </div>
          <button className={styles.ModalCloseBU} onClick={() => setCancelModalOpen(false)} aria-label="Close">
            <X />
          </button>
        </DialogTitle>
        <DialogContent className={styles.ModalBody}>
          <div className={`${styles.ModalIconWrap} ${styles.Danger}`}>
            <AlertTriangle size={20} />
          </div>
          <p className={styles.ModalText}>This can't be undone. The booking will be marked as cancelled.</p>
        </DialogContent>
        <DialogActions className={styles.ModalActions}>
          <Button className={styles.ModalBtnGhost} onClick={() => setCancelModalOpen(false)}>
            Keep booking
          </Button>
          <Button className={styles.ModalBtnDanger} onClick={cancelBooking}>
            Cancel booking
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withCustomerAuth(MyBookings);